<template>
  <b-field class="file">
    <b-tooltip :label="$t('UploadFileToolTip')" type="is-info">
      <b-button size="is-small" @click="openHelpPage">
        <b-icon icon="question-circle"></b-icon>
      </b-button>
    </b-tooltip>
    <b-upload
      v-model="file"
      @update:modelValue="revenueData"
      data-test-id="fileAttachtButton"
      :disabled="disabled"
      accept=".xlsx"
      expanded
    >
      <a class="button is-primary is-small">
        <span>{{ $t("ClickToUpload") }}</span>
        <b-icon icon="file-upload"></b-icon>
      </a>
    </b-upload>
    <span class="file-name" v-if="file">{{ file.name }}</span>
  </b-field>
</template>
<script lang="ts">
import { defineComponent, type PropType } from "vue";
import revenueService from "@/services/revenueService";
import type { Shop } from "@/stores/app";

type UploadedRevenueRow = Awaited<
  ReturnType<typeof revenueService.transformFileToObject>
>[number];
type RevenueValidationError =
  ReturnType<typeof revenueService.validateRevenueData>["errors"][number];
type RevenueValidationErrorGroup = RevenueValidationError[];

export default defineComponent({
  props: {
    shopsList: {
      type: Array as PropType<Shop[] | null>,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    "file-uploaded": (payload: UploadedRevenueRow[]) => Array.isArray(payload),
    error: (payload: RevenueValidationErrorGroup[]) => Array.isArray(payload),
  },
  data() {
    return {
      file: null as File | null,
      fileContent: [] as UploadedRevenueRow[],
      errors: [] as RevenueValidationErrorGroup[],
    };
  },

  methods: {
    async revenueData(uploadedFile: File | File[] | null) {
      const selectedFile = Array.isArray(uploadedFile)
        ? uploadedFile[0] ?? null
        : uploadedFile;

      this.file = selectedFile;

      if (selectedFile) {
        const data = await revenueService.transformFileToObject(selectedFile);
        this.setFileContent(data);
      } else {
        this.setErrors(revenueService.createError("ErrorEmptyFile"));
        this.fileContent = [];
      }
    },
    setFileContent(result: UploadedRevenueRow[]) {
      this.fileContent = result;
    },
    setErrors(error: RevenueValidationError | RevenueValidationErrorGroup) {
      this.errors.push(Array.isArray(error) ? error : [error]);
    },
    validateRevenueData(revenues: UploadedRevenueRow[]) {
      const validationResult = revenueService.validateRevenueData(
        revenues,
        this.shopsList ?? []
      );
      if (validationResult.success === false) {
        this.setErrors(validationResult.errors);
      }
      return validationResult.success;
    },
    openHelpPage() {
      window.open(
        "https://www.mallshoplink.com/report-via-file?lang=he",
        "_blank"
      );
    },
  },
  watch: {
    fileContent: {
      handler(fileContent: UploadedRevenueRow[]) {
        if (fileContent) {
          if (this.validateRevenueData(fileContent)) {
            this.$emit("file-uploaded", fileContent);
          } else {
            this.file = null;
            this.$emit("error", this.errors);
            this.errors = [];
          }
        }
      },
    },
  },
});
</script>

